import { Cubit } from "blac";
import { formatBytes } from "@9amhealth/shared";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { fileState } from "src/state/state";
import FilesCubit from "src/state/FilesCubit/FilesCubit";

const PREVIEWABLE_MIME_TYPES = new Set(["image/jpeg", "image/png"]);
const MAX_PREVIEW_DIMENSION = 2048;
const FALLBACK_FILENAME_PREFIX = "file_";
const FALLBACK_TYPE_LABEL = "FILE";

const MIME_TO_EXTENSION: Record<string, string> = {
  "image/jpeg": ".jpg",
  "image/png": ".png"
};

export interface FileViewerPopupState {
  isOpen: boolean;
  fileId: string | null;
  filename: string | null;
  mimeType: string | null;
  fileSize: number | null;
  previewUrl: string | null;
  previewBlob: Blob | null;
  loading: boolean;
  isPreviewable: boolean;
  fileTypeLabel: string;
  fileSizeLabel: string;
}

const INITIAL_STATE: FileViewerPopupState = {
  isOpen: false,
  fileId: null,
  filename: null,
  mimeType: null,
  fileSize: null,
  previewUrl: null,
  previewBlob: null,
  loading: false,
  isPreviewable: false,
  fileTypeLabel: "",
  fileSizeLabel: ""
};

export default class FileViewerPopupBloc extends Cubit<FileViewerPopupState> {
  constructor() {
    super(INITIAL_STATE);
  }

  // ── Public lifecycle ──

  open = async (fileId: string): Promise<void> => {
    const metadata = await fileState.getFileMetadata(fileId);

    const mimeType = metadata?.contentType ?? null;
    const filename =
      metadata?.filename ?? `${FALLBACK_FILENAME_PREFIX}${fileId}`;
    const fileSize = metadata?.contentLength ?? null;
    const isPreviewable =
      mimeType !== null && PREVIEWABLE_MIME_TYPES.has(mimeType);

    this.emit({
      isOpen: true,
      fileId,
      filename,
      mimeType,
      fileSize,
      previewUrl: null,
      previewBlob: null,
      loading: isPreviewable,
      isPreviewable,
      fileTypeLabel: FileViewerPopupBloc.deriveTypeLabel(mimeType, filename),
      fileSizeLabel: fileSize ? formatBytes(fileSize) : ""
    });

    if (isPreviewable) {
      void this.loadPreview(fileId);
    }
  };

  close = (): void => {
    if (this.state.previewUrl) {
      URL.revokeObjectURL(this.state.previewUrl);
    }
    this.emit({ ...INITIAL_STATE });
  };

  // ── Public actions ──

  download = (): void => {
    const { previewBlob, isPreviewable, fileId } = this.state;

    if (isPreviewable && previewBlob) {
      void FilesCubit.downloadFile(previewBlob, this.previewFilename);
      return;
    }

    if (fileId) {
      void this.transferOriginal(fileId, "download");
    }
  };

  share = async (): Promise<void> => {
    const { previewBlob, isPreviewable, fileId } = this.state;

    if (isPreviewable && previewBlob) {
      await FilesCubit.shareFile(previewBlob, this.previewFilename);
      return;
    }

    if (fileId) {
      await this.transferOriginal(fileId, "share");
    }
  };

  // ── Private: preview loading ──

  private loadPreview = async (fileId: string): Promise<void> => {
    try {
      const { w, h } = FileViewerPopupBloc.computePreviewDimensions();
      const response = await FilesCubit.requestDownloadFilePreview(
        fileId,
        w,
        h
      );

      if (!response.ok) {
        throw new Error(`Preview fetch failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      if (!this.isStillOpen(fileId)) {
        URL.revokeObjectURL(url);
        return;
      }

      this.emit({
        ...this.state,
        previewUrl: url,
        previewBlob: blob,
        loading: false
      });
    } catch (error) {
      reportErrorSentry(error);
      if (this.isStillOpen(fileId)) {
        this.emit({
          ...this.state,
          loading: false,
          isPreviewable: false
        });
      }
    }
  };

  // ── Private: original file transfer ──

  private transferOriginal = async (
    fileId: string,
    action: "download" | "share"
  ): Promise<void> => {
    const result = await FilesCubit.downloadFileWithOverlay(fileId);
    if (!result) {
      return;
    }

    const filename =
      result.metadata?.filename ??
      this.state.filename ??
      `${FALLBACK_FILENAME_PREFIX}${fileId}`;

    if (action === "download") {
      await FilesCubit.downloadFile(result.blob, filename);
    } else {
      await FilesCubit.shareFile(result.blob, filename);
    }
  };

  // ── Private: helpers ──

  private isStillOpen(fileId: string): boolean {
    return this.state.isOpen && this.state.fileId === fileId;
  }

  private get previewFilename(): string {
    const { filename, mimeType } = this.state;
    const base = filename ?? FALLBACK_FILENAME_PREFIX;
    const ext = (mimeType && MIME_TO_EXTENSION[mimeType]) ?? ".jpg";
    if (base.toLowerCase().endsWith(ext)) {
      return base;
    }
    return `${base}${ext}`;
  }

  // ── Static pure helpers ──

  private static computePreviewDimensions(): { w: number; h: number } {
    const dpr = window.devicePixelRatio || 1;
    return {
      w: Math.round(Math.min(window.innerWidth * dpr, MAX_PREVIEW_DIMENSION)),
      h: Math.round(Math.min(window.innerHeight * dpr, MAX_PREVIEW_DIMENSION))
    };
  }

  private static deriveTypeLabel(
    mimeType: string | null,
    filename: string | null
  ): string {
    if (mimeType) {
      const subtype = mimeType.split("/")[1];
      if (subtype) {
        return subtype.toUpperCase();
      }
    }
    if (filename) {
      const ext = filename.split(".").pop();
      if (ext && ext !== filename) {
        return ext.toUpperCase();
      }
    }
    return FALLBACK_TYPE_LABEL;
  }
}

export const FileViewerPopupController = new FileViewerPopupBloc();
